import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { RefreshCw, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { aiRewrite } from "@/lib/ai";

/**
 * Editable draft review panel. Each section can be rewritten by Claude with
 * different action (rewrite/shorten/expand/regenerate) and tone.
 *
 * Props:
 *  - sessionId
 *  - draft         — the full generation.data object
 *  - onDraftChange — parent setter (we mutate immutably)
 */
const TONES = [
  { v: "professional", label: "Professional" },
  { v: "friendly",     label: "Friendly" },
  { v: "investor",     label: "Investor" },
];

const ACTIONS = [
  { v: "rewrite",    label: "Rewrite" },
  { v: "shorten",    label: "Shorten" },
  { v: "expand",     label: "Expand" },
  { v: "regenerate", label: "Regenerate" },
];

const SectionCard = ({ label, sessionId, sectionKey, value, onChange, isMarkdown = false }) => {
  const [action, setAction] = useState("rewrite");
  const [tone, setTone] = useState("professional");
  const [busy, setBusy] = useState(false);

  const rewrite = async () => {
    if (typeof value !== "string" || !value.trim()) return;
    setBusy(true);
    try {
      const res = await aiRewrite(sessionId, {
        section: sectionKey,
        current_text: value,
        action,
        tone,
      });
      onChange(res.text);
      toast.success(`${label} rewritten (${tone})`);
    } catch (e) {
      toast.error(e.message || "Rewrite failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-warm-200 rounded-2xl bg-white p-4 space-y-3" data-testid={`draft-section-${sectionKey}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-saffron-700">{label}</div>
        <div className="flex items-center gap-1.5">
          <SelectPill value={action} onChange={setAction} options={ACTIONS} testid={`action-${sectionKey}`} />
          <SelectPill value={tone} onChange={setTone} options={TONES} testid={`tone-${sectionKey}`} />
          <button
            onClick={rewrite}
            disabled={busy || !value?.trim()}
            data-testid={`rewrite-${sectionKey}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-warm-200 bg-white px-2.5 h-8 text-xs font-semibold text-navy-700 hover:border-saffron-300 hover:text-saffron-700 disabled:opacity-50 transition"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Apply
          </button>
        </div>
      </div>

      {isMarkdown ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.max(6, Math.min(20, (value?.split("\n").length || 6)))}
          className="w-full resize-y rounded-xl border border-warm-200 bg-white px-3 py-2.5 text-sm text-navy-800 leading-[1.7] focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition font-mono"
        />
      ) : (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={value && value.length > 120 ? 4 : 2}
          className="w-full resize-y rounded-xl border border-warm-200 bg-white px-3 py-2.5 text-sm text-navy-800 focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition"
        />
      )}

      {isMarkdown && value && (
        <details className="text-xs">
          <summary className="cursor-pointer text-navy-500 hover:text-navy-700 inline-flex items-center gap-1">
            <ChevronDown className="h-3 w-3" /> Preview
          </summary>
          <div className="prose prose-sm max-w-none mt-2 prose-headings:font-display prose-headings:text-navy-800 prose-headings:tracking-tight">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          </div>
        </details>
      )}
    </div>
  );
};

const SelectPill = ({ value, onChange, options, testid }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="appearance-none rounded-full border border-warm-200 bg-white pl-3 pr-7 h-8 text-xs font-semibold text-navy-700 hover:border-saffron-300 focus:outline-none cursor-pointer"
    >
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
    <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-navy-500" />
  </div>
);

const CampaignDraftReview = ({ sessionId, draft, onDraftChange }) => {
  const setField = (key, value) => onDraftChange({ ...draft, [key]: value });

  return (
    <div className="p-4 sm:p-5 space-y-4">
      <SectionCard label="Title"          sessionId={sessionId} sectionKey="title"          value={draft.title}          onChange={(v) => setField("title", v)} />
      <SectionCard label="Tagline"        sessionId={sessionId} sectionKey="tagline"        value={draft.tagline}        onChange={(v) => setField("tagline", v)} />
      <SectionCard label="Elevator pitch" sessionId={sessionId} sectionKey="elevator_pitch" value={draft.elevator_pitch} onChange={(v) => setField("elevator_pitch", v)} />
      <SectionCard label="Story"          sessionId={sessionId} sectionKey="story"          value={draft.story}          onChange={(v) => setField("story", v)} isMarkdown />
      <SectionCard label="Why it matters" sessionId={sessionId} sectionKey="why_it_matters" value={draft.why_it_matters} onChange={(v) => setField("why_it_matters", v)} isMarkdown />
      <SectionCard label="Risks & challenges" sessionId={sessionId} sectionKey="risks_and_challenges" value={draft.risks_and_challenges} onChange={(v) => setField("risks_and_challenges", v)} isMarkdown />
      <SectionCard label="Call to action" sessionId={sessionId} sectionKey="call_to_action" value={draft.call_to_action} onChange={(v) => setField("call_to_action", v)} />

      <div className="border border-warm-200 rounded-2xl bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-saffron-700 mb-2">Key features</div>
        <ul className="space-y-1 text-sm text-navy-800">
          {(draft.key_features || []).map((f, i) => <li key={i} className="flex gap-2"><span className="text-saffron-600">•</span>{f}</li>)}
        </ul>
      </div>

      <div className="border border-warm-200 rounded-2xl bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-saffron-700 mb-2">Reward tiers ({(draft.reward_tiers || []).length})</div>
        <div className="space-y-2">
          {(draft.reward_tiers || []).map((r, i) => (
            <div key={i} className="flex items-baseline justify-between gap-3 border-t border-warm-100 first:border-t-0 pt-2 first:pt-0">
              <div className="min-w-0">
                <div className="font-semibold text-navy-800 text-sm truncate">{r.title}</div>
                <div className="text-xs text-navy-500 line-clamp-2">{r.description}</div>
              </div>
              <div className="font-display font-bold text-saffron-600 text-sm flex-shrink-0">₹{Number(r.price || 0).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      {draft.stretch_goals?.length ? (
        <div className="border border-warm-200 rounded-2xl bg-white p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-saffron-700 mb-2">Stretch goals</div>
          <ul className="space-y-2 text-sm text-navy-800">
            {draft.stretch_goals.map((g, i) => (
              <li key={i}><span className="font-semibold text-navy-800">₹{Number(g.amount || 0).toLocaleString()} · {g.label}</span> — <span className="text-navy-600">{g.description}</span></li>
            ))}
          </ul>
        </div>
      ) : null}

      {draft.timeline?.length ? (
        <div className="border border-warm-200 rounded-2xl bg-white p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-saffron-700 mb-2">Timeline</div>
          <ul className="space-y-1 text-sm text-navy-800">
            {draft.timeline.map((t, i) => (
              <li key={i}><span className="font-semibold">{t.phase}</span> — <span className="text-navy-500">{t.when}</span></li>
            ))}
          </ul>
        </div>
      ) : null}

      {draft.faqs?.length ? (
        <div className="border border-warm-200 rounded-2xl bg-white p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-saffron-700 mb-2">FAQs</div>
          <div className="space-y-2 text-sm">
            {draft.faqs.map((f, i) => (
              <details key={i} className="rounded-lg">
                <summary className="cursor-pointer font-semibold text-navy-800 py-1">{f.q}</summary>
                <p className="mt-1 text-navy-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <div className="border border-warm-200 rounded-2xl bg-white p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-saffron-700 mb-1">Suggested goal</div>
          <div className="font-display font-bold text-2xl text-navy-800">₹{Number(draft.suggested_funding_goal || 0).toLocaleString()}</div>
        </div>
        <div className="border border-warm-200 rounded-2xl bg-white p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-saffron-700 mb-1">Suggested category</div>
          <div className="font-display font-bold text-navy-800 capitalize">{draft.suggested_category || "—"}</div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDraftReview;
